/**
 * Presentation rules for the Support request timeline — pure, so they can be tested without a DOM.
 *
 * ── WHAT THIS IS, AND WHAT IT IS NOT ───────────────────────────────────────────────────────
 * The timeline is HTTP EVIDENCE: what the API was asked, and what it answered. It is not a
 * business-outcome interpreter. A `201` is rendered as `201`, never as "document uploaded" —
 * whether the document really arrived is a question for the business Activity Log beside it,
 * and for the diagnosed failures below it. Conflating the three is how a support executive ends
 * up confidently telling a barrister the opposite of what happened.
 *
 * Everything here reads the API's safe projection and formats it. Nothing reconstructs a URL,
 * re-derives an id, or infers meaning the backend did not record.
 */

/** Exactly the shape Phase 4B returns (RequestTimelineEntry in api/lib/support/investigation.ts). */
export interface RequestTimelineEntry {
  occurredAt: string;
  method: string | null;
  /** Already a route pattern (`/api/v1/documents/:id/preview`). Rendered verbatim. */
  path: string | null;
  statusCode: number;
  durationMs: number | null;
  correlationId: string | null;
  errorCode: string | null;
}

/** The cap the API applies (MAX_REQUEST_ROWS). Mirrored here only for the truncation wording. */
export const REQUEST_TIMELINE_CAP = 150;

export type RequestStatusTone = 'success' | 'redirect' | 'client' | 'server' | 'unknown';

/**
 * HTTP class → tone. Deliberately coarse: 2xx/4xx/5xx is the distinction a support executive
 * scanning a hundred rows actually needs, and a per-code palette would be noise.
 */
export function requestStatusTone(statusCode: number): RequestStatusTone {
  if (statusCode >= 200 && statusCode < 300) return 'success';
  if (statusCode >= 300 && statusCode < 400) return 'redirect';
  if (statusCode >= 400 && statusCode < 500) return 'client';
  if (statusCode >= 500) return 'server';
  return 'unknown';
}

/** The console's existing badge tokens — the same ones the status/priority badges already use. */
export const REQUEST_STATUS_CLASS: Record<RequestStatusTone, string> = {
  success: 'bg-plat-success-soft text-plat-success-text',
  redirect: 'bg-plat-neutral-soft text-plat-secondary',
  client: 'bg-plat-warning-soft text-plat-warning-text',
  server: 'bg-plat-danger-soft text-plat-danger-text',
  unknown: 'bg-plat-neutral-soft text-plat-secondary',
};

export function requestStatusClass(statusCode: number): string {
  return REQUEST_STATUS_CLASS[requestStatusTone(statusCode)];
}

/**
 * Is this the exact request the customer watched fail?
 *
 * The ticket carries `reportedCorrelationId`, captured by the browser at the moment of failure.
 * A match is highlighted; a non-match is not. Never guessed: a null or empty id on either side
 * matches nothing, because "probably this one" is worse than no answer in a diagnosis.
 */
export function isReportedRequest(
  entry: Pick<RequestTimelineEntry, 'correlationId'>,
  reportedCorrelationId: string | null | undefined,
): boolean {
  if (!reportedCorrelationId || !entry.correlationId) return false;
  return entry.correlationId === reportedCorrelationId;
}

/** `224 ms`, `1.8 s`, or null when the row predates duration capture. */
export function formatRequestDuration(durationMs: number | null): string | null {
  if (durationMs === null || Number.isNaN(durationMs)) return null;
  if (durationMs >= 1000) return `${(durationMs / 1000).toFixed(1)} s`;
  return `${Math.round(durationMs)} ms`;
}

/** Clock only — the investigation window is hours, so the date would repeat on every row. */
export function formatRequestTime(occurredAt: string): string {
  return new Date(occurredAt).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

/**
 * The truncation notice, or null when nothing was dropped.
 *
 * WORDING MATTERS. The API orders newest-first and takes the cap, then reverses for display — so
 * what survives is the MOST RECENT 150, not the first 150. Saying "the first 150" would tell a
 * support executive that the earliest events are present when they are exactly the ones missing,
 * which on a timeline is the difference between "the upload succeeded" and "we never saw it".
 */
export function requestTruncationNotice(truncated: boolean): string | null {
  if (!truncated) return null;
  return `Showing the ${REQUEST_TIMELINE_CAP} most recent request events in this window — there were more, and the earliest are not shown.`;
}

/**
 * Shown when the collection is empty.
 *
 * Carefully NOT "no requests occurred" — the capture policy is selective (only documents,
 * matters and a short list of mutations), so an empty timeline means nothing qualifying was
 * captured, which is not the same claim.
 */
export const REQUEST_TIMELINE_EMPTY =
  'No request diagnostics were captured for this investigation window.';
